import { Link, createFileRoute } from '@tanstack/react-router';
import { Brain, MessageSquare, Sparkles, UserCircle, Users, Zap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import LandingNavigation from '@/components/landing-navigation';

export const Route = createFileRoute('/')({
  component: LandingPage
});

function LandingPage() {
  const coreFeatures = [
    {
      icon: MessageSquare,
      title: 'Collectez via des campagnes',
      description:
        "Créez des campagnes de collecte sous forme de formulaire classique ou d'interview vocale automatisée. Vos utilisateurs partagent leurs idées et feedbacks de manière naturelle, sans friction.",
      gradient: 'from-purple-500 to-blue-500'
    },
    {
      icon: Brain,
      title: 'Organisez et priorisez',
      description:
        "L'IA classe automatiquement, consolide les doublons, organise par thèmes et priorise selon l'impact potentiel. Transformez des centaines d'idées en une roadmap claire et actionnable.",
      gradient: 'from-blue-500 to-cyan-500'
    },
    {
      icon: Users,
      title: 'Validez avec vos personas',
      description:
        'Chaque idée est confrontée à vos personas — des simulations IA de vos clients types. Découvrez en un clic quelles idées résonnent vraiment avec vos utilisateurs avant de développer.',
      gradient: 'from-cyan-500 to-teal-500'
    }
  ];

  const personaBenefits = [
    {
      icon: UserCircle,
      title: 'Créez vos Personas',
      description:
        'Définissez vos utilisateurs types manuellement ou générez-les automatiquement. Chaque persona représente un profil utilisateur distinct.',
      gradient: 'from-purple-500 to-pink-500'
    },
    {
      icon: Brain,
      title: 'Simulation des réactions',
      description:
        "Chaque idée est confrontée à vos personas. Que pense cette idée du point de vue d'un expert ? D'un débutant ? D'un décideur ?",
      gradient: 'from-pink-500 to-orange-500'
    },
    {
      icon: Sparkles,
      title: "Avis d'expert automatique",
      description:
        "Le système identifie les points faibles, met en lumière les conflits et offre un avis d'expert pour améliorer l'idée selon les attentes de chaque persona.",
      gradient: 'from-orange-500 to-secondary'
    }
  ];

  return (
    <div className="min-h-screen bg-linear-to-b from-background to-muted/20">
      <LandingNavigation />
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center space-y-8 animate-fade-in-up">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 border border-primary/20 rounded-full text-sm font-medium text-primary">
              <Sparkles className="h-4 w-4" />
              Collectez plus d&apos;idées. Prenez de meilleures décisions.
            </div>

            <h1 className="text-5xl md:text-7xl font-bold tracking-tight">
              Les idées rencontrent vos utilisateurs{' '}
              <span className="bg-linear-to-r from-primary via-purple-600 to-secondary bg-clip-text text-transparent">
                24h/24
              </span>
            </h1>

            <p className="text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
              Les idées sont le carburant de tout produit. Mais sans structure, elles s&apos;empilent, se dupliquent, se
              perdent. Pourtant, il suffit parfois d&apos;une seule idée pour tout changer.
              <span className="text-foreground font-medium">
                {' '}
                BeSpark organise ce flot pour faire surgir l&apos;étincelle capable de propulser votre projet
              </span>
              .
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4">
              <Link to="/register">
                <Button variant="hero" size="lg">
                  Essayer maintenant
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Core Features Section */}
      <section className="py-20 px-4 bg-muted/30">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">La méthode BeSpark</h2>
            <p className="text-muted-foreground text-lg max-w-3xl mx-auto leading-relaxed">
              Trois étapes simples pour transformer le chaos des idées en décisions éclairées. De la collecte à la
              validation, chaque étape est orchestrée par l&apos;IA pour vous faire gagner du temps et prendre les
              bonnes décisions.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {coreFeatures.map((feature, index) => (
              <Card
                key={index}
                className="border-2 hover:border-primary/50 transition-all duration-300 hover:shadow-lg hover:-translate-y-1 animate-fade-in-up"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <CardContent className="p-6 space-y-4">
                  <div className="flex items-center gap-3 mb-2">
                    <div
                      className={`w-10 h-10 rounded-lg bg-linear-to-br ${feature.gradient} p-2.5 animate-float flex items-center justify-center text-white font-bold text-lg`}
                    >
                      {index + 1}
                    </div>
                    <div className={`w-12 h-12 rounded-lg bg-linear-to-br ${feature.gradient} p-2.5 animate-float`}>
                      <feature.icon className="h-full w-full text-white" />
                    </div>
                  </div>
                  <h3 className="text-xl font-semibold">{feature.title}</h3>
                  <p className="text-muted-foreground leading-relaxed">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Personas Revolution Section */}
      <section className="py-20 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-secondary/10 border border-secondary/20 rounded-full text-sm font-medium text-secondary mb-6">
              <Zap className="h-4 w-4" />
              La vraie révolution
            </div>
            <h2 className="text-3xl md:text-5xl font-bold mb-6">
              Les{' '}
              <span className="bg-linear-to-r from-primary to-secondary bg-clip-text text-transparent">Personas</span>
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
              Vous créez vos utilisateurs types - ou vous les générez automatiquement. Et chaque idée, chaque
              proposition, chaque intuition est confrontée à ces profils.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            {personaBenefits.map((benefit, index) => (
              <Card
                key={index}
                className="border-2 hover:border-primary/50 transition-all duration-300 hover:shadow-lg hover:-translate-y-1 animate-fade-in-up"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <CardContent className="p-6 space-y-4">
                  <div className={`w-12 h-12 rounded-lg bg-linear-to-br ${benefit.gradient} p-2.5 animate-float`}>
                    <benefit.icon className="h-full w-full text-white" />
                  </div>
                  <h3 className="text-xl font-semibold">{benefit.title}</h3>
                  <p className="text-muted-foreground leading-relaxed">{benefit.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <Card className="bg-linear-to-br from-primary/10 via-purple-500/10 to-secondary/10 border-primary/20">
            <CardContent className="p-8 md:p-12">
              <div className="text-center space-y-6">
                <Users className="h-16 w-16 mx-auto text-primary animate-pulse-glow" />
                <h3 className="text-2xl md:text-3xl font-bold">Une place publique numérique</h3>
                <p className="text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
                  BeSpark devient alors une place publique numérique, où vos utilisateurs réels — et ceux que vous
                  ciblez — participent automatiquement au développement de votre produit.
                </p>
                <p className="text-lg font-medium text-foreground max-w-2xl mx-auto">
                  Une manière profondément humaine et pourtant amplifiée par l&apos;IA de prendre de meilleures
                  décisions.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 px-4 bg-muted/30">
        <div className="container mx-auto max-w-5xl">
          <div className="bg-linear-to-r from-primary via-purple-600 to-secondary rounded-2xl p-px">
            <div className="bg-background rounded-2xl p-8 md:p-12">
              <div className="flex flex-col md:flex-row items-center justify-between gap-8">
                <div className="flex-1 text-center md:text-left space-y-4">
                  <h2 className="text-3xl md:text-4xl font-bold">Vous ne décidez plus seul</h2>
                  <p className="text-muted-foreground text-lg leading-relaxed">
                    Avec BeSpark, vous décidez avec vos utilisateurs, même lorsqu&apos;ils ne sont pas là.
                  </p>
                </div>
                <div className="shrink-0">
                  <Link to="/login">
                    <Button variant="spark" size="lg" className="text-base px-8 py-6">
                      Commencer maintenant
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
